/**
 * usePostMediaEdit — the media-zone edit brain for the post inspector.
 *
 * Three flows, all converging on the server-authoritative
 * /api/generate-image { postId } write-back (Phase 2):
 *
 *   editSlot          — one-tap / Describe edit of a slot.
 *                       scheduled → stage a pendingCompare (Keep/Revert).
 *                       draft/etc → write back immediately, archive prior to
 *                       media_keys.history[], toast Undo.
 *   generateVariations— n candidate renders that DO NOT write back. The route
 *                       is called WITHOUT postId, so nothing touches the row.
 *   pickCandidate     — the user's chosen candidate writes back via replace-slot
 *                       (postId path), archiving the prior media to history.
 *
 * Compare-vs-instant is decided once by compareModeForStatus (Task 33).
 *
 * CAS (Task 13/38 seam): callers may pass `expectedUpdatedAt` so that every
 * postId write-back carries the row's last-known updated_at timestamp. The
 * route rejects the write if the row has since been mutated (e.g. a publish
 * cron flipping the row mid-compare). Leaving the field optional means an
 * absent arg is a safe no-op — the route skips the CAS guard when the field
 * is absent.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { compareModeForStatus } from './compareMode';
import type { MediaKeysBag } from './mediaKeysWriter';

import { useEffectiveLockedFields } from '@/hooks/useEffectiveLockedFields';
import { useInvalidatePostSurfaces } from '@/hooks/useInvalidatePostSurfaces';
import { friendlyMessageFromError } from '@/lib/errors/friendly-errors';
import { type EditIntentKind, assertEditAllowed } from '@/lib/posts/edit-guardrail';
import { isLockedEditError, showLockedEditToast } from '@/lib/posts/locked-edit-toast';
import { type MediaHistoryEntry, pushMediaHistory } from '@/lib/posts/media-history';
import { postService } from '@/services/data';

/** Map a generate-image editMode to the BI-5 edit-intent vocabulary so the
 *  client pre-flight can deny a recolor/logo edit before the network call.
 *  Anything that isn't a deterministic recolor/logo aspect is a generic /
 *  manual media edit and stays allowed (media is not a monolith). */
function editModeToIntentKind(editMode: string | undefined): EditIntentKind {
  switch (editMode) {
    case 'recolor':
      return 'recolor';
    case 'logo':
    case 'logo-swap':
      return 'logo-swap';
    case 'logo-overlay':
      return 'logo-overlay';
    default:
      return 'manual-media-swap';
  }
}

const VIDEO_RE = /\.(mp4|mov|webm|m4v)(\?|$)/i;

/** Shared toast branch for the three write-back flows below (editSlot,
 *  pickCandidate, keepRevised): a successful write-back that hit the parked
 *  media-library-insert failure (Task 10) still surfaces as a warning, not a
 *  silent success — only the copy differs per call site. */
function reportMediaOutcome(
  toastId: string,
  result: { mediaLibraryWriteFailed?: boolean },
  successMsg: Parameters<typeof toast.success>[0],
  warnMsg: string,
  successOpts?: Parameters<typeof toast.success>[1],
): void {
  if (result.mediaLibraryWriteFailed) {
    toast.warning(warnMsg, { id: toastId });
  } else {
    toast.success(successMsg, { id: toastId, ...successOpts });
  }
}

export interface VariationCandidate {
  url: string;
  seed?: string | number;
  model?: string;
  prompt: string;
}
export interface PendingCompare {
  slotIndex: number;
  originalUrl: string;
  revisedUrl: string;
  prompt: string;
  model?: string;
  seed?: string | number;
}
export interface UsePostMediaEditArgs {
  postId: string;
  status: string | null | undefined;
  userId: string | null | undefined;
  mediaUrls: string[];
  accessToken: string | null | undefined;
  aspectRatio?: string;
  mediaKeys: unknown;
  /** CAS guard (Task 13/38): the row's last-known updated_at timestamp.
   *  When present, every postId write-back passes it to the route so the
   *  route can reject a stale write (e.g. publish cron mutated the row
   *  while the user was reviewing a compare). Absent = no guard (safe
   *  default; Task 38 wires the value in). */
  expectedUpdatedAt?: string;
  /** BI-4-05 fixes-as-found: forwarded to every /api/generate-image call so
   *  edit/vary/pickCandidate apply brand styling like generateSlide does. */
  brandKit?: Record<string, unknown> | null;
  /** PostBody's serialized media_keys committer (pre-merge review finding #4).
   *  When provided, the history archive routes its write through the shared
   *  commit queue — the transform is applied to FRESH server keys inside the
   *  queue run, so archiving can never whole-object-write a stale snapshot
   *  over a just-saved placement/aspect/framing change. Absent (bare-hook
   *  usage in tests) → legacy direct write. */
  commitMediaKeys?: (transform: (fresh: MediaKeysBag) => MediaKeysBag) => Promise<unknown>;
}
export interface UsePostMediaEdit {
  busySlot: number | null;
  pendingCompare: PendingCompare | null;
  candidates: { slotIndex: number; items: VariationCandidate[] } | null;
  editSlot(
    slotIndex: number,
    prompt: string,
    opts?: { editMode?: string; keepIdentity?: boolean },
  ): Promise<void>;
  generateVariations(
    slotIndex: number,
    prompt: string,
    n: number,
    opts?: { keepIdentity?: boolean },
  ): Promise<void>;
  pickCandidate(slotIndex: number, candidate: VariationCandidate): Promise<void>;
  keepRevised(): Promise<void>;
  revertCompare(): Promise<void>;
  dismissCandidates(): void;
}

interface GenResult {
  imageUrl?: string;
  image_url?: string;
  imageUrls?: string[];
  model?: string;
  seed?: string | number;
  prompt?: string;
  postUpdated?: boolean;
  mediaUrls?: string[];
  reason?: string;
  state?: string;
  error?: string;
  /** Task 10: the image was applied and the post write-back landed, but the
   *  media-library row insert failed — the file exists in storage without a
   *  library entry. Non-fatal by design (see generate-image.post.ts
   *  mediaLibraryOutcome); the client must still surface it, not treat the
   *  response as unqualified success. */
  mediaLibraryWriteFailed?: boolean;
}

export function usePostMediaEdit(args: UsePostMediaEditArgs): UsePostMediaEdit {
  const {
    postId,
    status,
    userId,
    mediaUrls,
    accessToken,
    aspectRatio,
    mediaKeys,
    // TODO(Task 38): echo the route's committed `updated_at` back into a
    // hook-held CAS value after each write-back, so back-to-back edits don't
    // false-lock on a stale token. Until then callers pass the last-known value.
    expectedUpdatedAt,
    brandKit,
    commitMediaKeys,
  } = args;
  const invalidate = useInvalidatePostSurfaces();
  // BI-5-03: inherited (ancestor-only) locks — the enforcement set. Empty for
  // all consumer/corporate-own kits, so the pre-flight below is a no-op there.
  const { inheritedLockedFields } = useEffectiveLockedFields();
  const [busySlot, setBusySlot] = useState<number | null>(null);
  const [pendingCompare, setPendingCompare] = useState<PendingCompare | null>(null);
  const [candidates, setCandidates] = useState<{
    slotIndex: number;
    items: VariationCandidate[];
  } | null>(null);

  // Re-entrancy guard. `busySlot` is stale within the same tick, so a ref is
  // required to reject a second async entry before the first has resolved.
  const inFlight = useRef(false);

  // History is built from the LAST-KNOWN media_keys, not the (possibly stale)
  // render-prop closure: back-to-back edits before the async invalidate refetch
  // lands must accumulate history instead of dropping the first archived entry.
  // A parent refetch re-syncs the ref to the fresh prop via the effect below.
  const lastKeysRef = useRef<unknown>(mediaKeys);
  useEffect(() => {
    lastKeysRef.current = mediaKeys;
  }, [mediaKeys]);

  const callRoute = async (body: Record<string, unknown>): Promise<GenResult> => {
    const response = await fetch('/api/generate-image', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({
        aspectRatio: aspectRatio || '1:1',
        imageModel: 'auto',
        ...(brandKit ? { brandKit } : {}),
        ...body,
      }),
    });
    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as {
        error?: string;
        errorCode?: string;
      };
      // Preserve the server's structured errorCode (e.g. BI-5-02's 403
      // `edit_not_allowed`) so the catch layer can map it to the lock toast
      // instead of the generic "Edit failed" copy.
      const routeError = new Error(err.error || `Edit failed (${response.status})`) as Error & {
        errorCode?: string;
      };
      if (err.errorCode) routeError.errorCode = err.errorCode;
      throw routeError;
    }
    const result = (await response.json()) as GenResult;
    if (result.state === 'error') {
      // Preserve the server's structured errorCode (200 + state:'error' path) so
      // the catch layer maps it to a friendly message instead of the raw string.
      const routeError = new Error(result.error || 'Edit failed.') as Error & {
        errorCode?: string;
      };
      const code = (result as { errorCode?: string }).errorCode;
      if (code) routeError.errorCode = code;
      throw routeError;
    }
    return result;
  };

  // Archive the prior slot media to media_keys.history[], then fan invalidation.
  const archiveAndInvalidate = async (entry: MediaHistoryEntry) => {
    // lastKeysRef still provides same-tick ENTRY continuity (sequential edits
    // before the invalidate refetch lands must accumulate history instead of
    // dropping the first archived entry) — advance it immediately. But the
    // WRITE must not trust it: when the shared committer is available (review
    // finding #4), the pushMediaHistory transform is re-applied to the FRESH
    // server keys inside PostBody's serialized commit queue, so archiving can
    // never whole-object-write this stale-able snapshot over a just-saved
    // placement/aspect/framing change on the same JSONB column.
    lastKeysRef.current = pushMediaHistory(lastKeysRef.current, entry);
    if (commitMediaKeys) {
      await commitMediaKeys((fresh) => pushMediaHistory(fresh, entry) as MediaKeysBag);
    } else {
      await postService.updatePost(postId, { media_keys: lastKeysRef.current as never });
    }
    // Invalidate on every successful write, even if userId is null/lagging —
    // otherwise the inspector goes stale. The invalidate hook requires a string;
    // the userId-independent surfaces (posts/media/etc.) still refresh.
    await invalidate({ userId: userId ?? '', postId });
  };

  const editSlot: UsePostMediaEdit['editSlot'] = async (slotIndex, prompt, opts) => {
    if (inFlight.current) return;
    const trimmed = prompt.trim();
    const sourceUrl = mediaUrls[slotIndex];
    if (!trimmed || !sourceUrl || VIDEO_RE.test(sourceUrl)) return;
    if (!accessToken) {
      toast.error('Sign in to edit media');
      return;
    }
    // BI-5-03 client pre-flight (UX layer — the /api/generate-image route is
    // the hard stop): deny a recolor/logo edit before spending a network call
    // when the corresponding aspect is inherited-locked. Empty inherited set →
    // no-op. Generic/background/manual edits are never blocked here.
    if (inheritedLockedFields.length > 0) {
      const guard = assertEditAllowed({
        lockedFields: inheritedLockedFields,
        field: 'media',
        intent: { kind: editModeToIntentKind(opts?.editMode) },
      });
      if (!guard.allowed) {
        showLockedEditToast();
        return;
      }
    }
    inFlight.current = true;
    setBusySlot(slotIndex);
    const toastId = `post-media-edit-${slotIndex}`;
    toast.loading('Editing…', { id: toastId });
    try {
      const compare = compareModeForStatus(status) === 'compare';
      const result = await callRoute({
        prompt: trimmed,
        editMode: opts?.editMode ?? 'inpaint',
        referenceImageUrl: sourceUrl,
        ...(opts?.keepIdentity ? { referenceImageRoles: ['person'] } : {}),
        // scheduled compare = render only (no write-back); instant = write-back.
        ...(compare
          ? {}
          : {
              postId,
              replaceSlot: slotIndex,
              mediaWriteMode: 'replace-slot',
              ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
            }),
      });
      const newUrl = result.imageUrl || result.image_url;
      if (!newUrl) throw new Error('No image returned.');
      if (compare) {
        // Review states are mutually exclusive: a Keep/Revert bar must never
        // co-exist with a candidate tray.
        setCandidates(null);
        setPendingCompare({
          slotIndex,
          originalUrl: sourceUrl,
          revisedUrl: newUrl,
          prompt: trimmed,
          model: result.model,
          seed: result.seed,
        });
        toast.dismiss(toastId);
        return;
      }
      if (result.postUpdated === false && result.reason === 'locked') {
        toast.error('Publishing now — saved to your library, not applied; reschedule to use it.', {
          id: toastId,
        });
        return;
      }
      await archiveAndInvalidate({
        url: sourceUrl,
        prompt: trimmed,
        model: result.model,
        seed: result.seed,
        createdAt: new Date().toISOString(),
      });
      reportMediaOutcome(
        toastId,
        result,
        'Image updated',
        'Image applied, but it was not saved to your library.',
        { action: { label: 'Undo', onClick: () => void revertToUrl(slotIndex, sourceUrl) } },
      );
    } catch (err) {
      if (isLockedEditError(err)) {
        toast.dismiss(toastId);
        showLockedEditToast();
      } else {
        console.error(
          '[post-media-edit] edit failed:',
          err instanceof Error ? err.message : String(err),
        );
        toast.error(friendlyMessageFromError(err), { id: toastId });
      }
    } finally {
      inFlight.current = false;
      setBusySlot(null);
    }
  };

  // Undo (instant path): re-write the slot back to the archived url via the
  // server route. The route is the authority — it enforces the lifecycle/CAS
  // guard, so we honor its verdict instead of doing a bare unguarded write that
  // could clobber a row the publish cron already flipped to publishing/published.
  const revertToUrl = async (slotIndex: number, url: string) => {
    const toastId = `post-media-undo-${slotIndex}`;
    try {
      const result = await callRoute({
        prompt: 'revert',
        postId,
        replaceSlot: slotIndex,
        mediaWriteMode: 'replace-slot',
        referenceImageUrl: url,
        editMode: 'restore-url',
        ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
      });
      if (result.postUpdated === false && result.reason === 'locked') {
        toast.error("Couldn't undo — this post is publishing.", { id: toastId });
        return;
      }
      // Route already reverted the row; just refresh the surfaces.
      await invalidate({ userId: userId ?? '', postId });
      toast.success('Reverted', { id: toastId });
    } catch {
      toast.error("Couldn't undo — please try again.", { id: toastId });
    }
  };

  const generateVariations: UsePostMediaEdit['generateVariations'] = async (
    slotIndex,
    prompt,
    n,
    opts,
  ) => {
    if (inFlight.current) return;
    const trimmed = prompt.trim();
    const sourceUrl = mediaUrls[slotIndex];
    if (!trimmed || !sourceUrl || VIDEO_RE.test(sourceUrl)) return;
    if (!accessToken) {
      toast.error('Sign in to edit media');
      return;
    }
    inFlight.current = true;
    // Review states are mutually exclusive: opening a candidate tray must clear
    // any pending Keep/Revert compare bar.
    setPendingCompare(null);
    setBusySlot(slotIndex);
    const toastId = `post-media-vary-${slotIndex}`;
    toast.loading('Generating variations…', { id: toastId });
    try {
      // NO postId — the route renders candidates and DOES NOT touch the row.
      const result = await callRoute({
        prompt: trimmed,
        editMode: 'inpaint',
        referenceImageUrl: sourceUrl,
        ...(opts?.keepIdentity ? { referenceImageRoles: ['person'] } : {}),
        n: Math.max(1, Math.min(4, Math.round(Number(n) || 1))),
      });
      const urls = (result.imageUrls?.length ? result.imageUrls : [result.imageUrl]).filter(
        (u): u is string => !!u,
      );
      if (urls.length === 0) throw new Error('No variations returned.');
      setCandidates({
        slotIndex,
        items: urls.map((url) => ({
          url,
          prompt: trimmed,
          model: result.model,
          seed: result.seed,
        })),
      });
      toast.dismiss(toastId);
    } catch (err) {
      console.error(
        '[post-media-edit] variation failed:',
        err instanceof Error ? err.message : String(err),
      );
      toast.error(friendlyMessageFromError(err), { id: toastId });
    } finally {
      inFlight.current = false;
      setBusySlot(null);
    }
  };

  const pickCandidate: UsePostMediaEdit['pickCandidate'] = async (slotIndex, candidate) => {
    if (inFlight.current) return;
    const sourceUrl = mediaUrls[slotIndex];
    if (!accessToken || !sourceUrl) return;
    inFlight.current = true;
    setBusySlot(slotIndex);
    const toastId = `post-media-pick-${slotIndex}`;
    toast.loading('Applying…', { id: toastId });
    try {
      // The pick writes back the chosen candidate via replace-slot. We pass the
      // candidate URL as the reference + a passthrough mode so the route persists
      // it without re-rolling a new image.
      const result = await callRoute({
        prompt: candidate.prompt,
        postId,
        replaceSlot: slotIndex,
        mediaWriteMode: 'replace-slot',
        referenceImageUrl: candidate.url,
        editMode: 'restore-url',
        seed: typeof candidate.seed === 'number' ? candidate.seed : undefined,
        ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
      });
      if (result.postUpdated === false && result.reason === 'locked') {
        toast.error('Publishing now — saved to your library, not applied; reschedule to use it.', {
          id: toastId,
        });
        return;
      }
      await archiveAndInvalidate({
        url: sourceUrl,
        prompt: candidate.prompt,
        model: candidate.model,
        seed: candidate.seed,
        createdAt: new Date().toISOString(),
      });
      setCandidates(null);
      // Same silent-success-on-failed-library-insert guard as editSlot above
      // (Task 10 parked finding): the post write-back genuinely succeeded, so
      // this isn't a hard error, but a failed library insert must still be
      // named — not swallowed behind a plain "Image updated".
      reportMediaOutcome(
        toastId,
        result,
        'Image updated',
        'Image applied, but it was not saved to your library.',
      );
    } catch (err) {
      if (isLockedEditError(err)) {
        toast.dismiss(toastId);
        showLockedEditToast();
      } else {
        console.error(
          '[post-media-edit] apply failed:',
          err instanceof Error ? err.message : String(err),
        );
        toast.error(friendlyMessageFromError(err), { id: toastId });
      }
    } finally {
      inFlight.current = false;
      setBusySlot(null);
    }
  };

  const keepRevised: UsePostMediaEdit['keepRevised'] = async () => {
    if (inFlight.current) return;
    if (!pendingCompare) return;
    inFlight.current = true;
    const { slotIndex, originalUrl, revisedUrl, prompt, model, seed } = pendingCompare;
    setBusySlot(slotIndex);
    const toastId = `post-media-keep-${slotIndex}`;
    toast.loading('Saving…', { id: toastId });
    try {
      const result = await callRoute({
        prompt,
        postId,
        replaceSlot: slotIndex,
        mediaWriteMode: 'replace-slot',
        referenceImageUrl: revisedUrl,
        editMode: 'restore-url',
        ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
      });
      if (result.postUpdated === false && result.reason === 'locked') {
        toast.error('Publishing now — saved to your library, not applied; reschedule to use it.', {
          id: toastId,
        });
        // Clear the dead Keep affordance so it can't re-fail forever.
        setPendingCompare(null);
        return;
      }
      await archiveAndInvalidate({
        url: originalUrl,
        prompt,
        model,
        seed,
        createdAt: new Date().toISOString(),
      });
      setPendingCompare(null);
      // Same silent-success-on-failed-library-insert guard as editSlot above
      // (Task 10 parked finding) — see pickCandidate's identical branch.
      reportMediaOutcome(
        toastId,
        result,
        'Kept the revision',
        'Kept the revision, but it was not saved to your library.',
      );
    } catch (err) {
      if (isLockedEditError(err)) {
        toast.dismiss(toastId);
        showLockedEditToast();
        setPendingCompare(null);
      } else {
        console.error(
          '[post-media-edit] save failed:',
          err instanceof Error ? err.message : String(err),
        );
        toast.error(friendlyMessageFromError(err), { id: toastId });
      }
    } finally {
      inFlight.current = false;
      setBusySlot(null);
    }
  };

  const revertCompare: UsePostMediaEdit['revertCompare'] = async () => {
    // Nothing was written back for a scheduled compare — discard the candidate.
    setPendingCompare(null);
  };

  const dismissCandidates = () => setCandidates(null);

  return {
    busySlot,
    pendingCompare,
    candidates,
    editSlot,
    generateVariations,
    pickCandidate,
    keepRevised,
    revertCompare,
    dismissCandidates,
  };
}
